import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add,
  Group,
  Person,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  Business,
  Assignment
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Team, User, Project, UserRole, TeamMember } from '../types';

interface TeamWithMembers extends Team {
  members?: TeamMember[];
  project?: Project;
}

const Teams: React.FC = () => {
  const { user, selectedOrganizationId } = useAuth();
  const [teams, setTeams] = useState<TeamWithMembers[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamWithMembers | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    projectId: '',
    members: [] as string[]
  });

  useEffect(() => {
    loadData();
  }, [selectedOrganizationId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load projects first
      const isRootUser = user?.role === UserRole.ROOT_USER;
      const isAdmin = user?.role && [UserRole.ADMIN, UserRole.SUPER_MANAGER].includes(user.role);
      
      let projectsData;
      if (isRootUser) {
        projectsData = await apiService.getProjects(selectedOrganizationId || undefined);
      } else if (isAdmin) {
        projectsData = await apiService.getProjects();
      } else {
        projectsData = await apiService.getUserProjects();
      }
      
      setProjects(projectsData || []);
      
      // Load users
      let usersData;
      if (isRootUser && selectedOrganizationId) {
        usersData = await apiService.getUsersByOrganization(selectedOrganizationId);
      } else {
        usersData = await apiService.getAllUsers();
      }
      setUsers(usersData || []);
      
      // Load teams for each project
      const allTeams: TeamWithMembers[] = [];
      for (const project of projectsData || []) {
        try {
          const projectTeams = await apiService.getTeams(project.id);
          allTeams.push(...projectTeams.map(team => ({
            ...team,
            project
          })));
        } catch (err) {
          console.error(`Failed to load teams for project ${project.name}:`, err);
        }
      }
      
      setTeams(allTeams);
    } catch (error) {
      console.error('Error loading teams data:', error);
      setError('Failed to load teams data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    try {
      await apiService.createTeam(formData);
      setOpenCreateModal(false);
      setFormData({ name: '', description: '', projectId: '', members: [] });
      loadData();
    } catch (error) {
      console.error('Error creating team:', error);
      setError('Failed to create team');
    }
  };

  const handleEditTeam = async () => {
    if (!selectedTeam) return;
    
    try {
      await apiService.updateTeam(selectedTeam.id, formData);
      setOpenEditModal(false);
      setSelectedTeam(null);
      setFormData({ name: '', description: '', projectId: '', members: [] });
      loadData();
    } catch (error) {
      console.error('Error updating team:', error);
      setError('Failed to update team');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    try {
      await apiService.deleteTeam(teamId);
      loadData();
    } catch (error) {
      console.error('Error deleting team:', error);
      setError('Failed to delete team');
    }
  };

  const openEditModalHandler = (team: TeamWithMembers) => {
    setSelectedTeam(team);
    setFormData({
      name: team.name,
      description: team.description || '',
      projectId: team.projectId,
      members: team.members?.map(m => m.userId) || []
    });
    setOpenEditModal(true);
  };

  const canManageTeams = user?.role && [
    UserRole.ADMIN,
    UserRole.SUPER_MANAGER,
    UserRole.PROJECT_MANAGER,
    UserRole.ROOT_USER
  ].includes(user.role);

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ 
        mb: 3,
        background: (theme) => theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: (theme) => theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        borderRadius: 2,
        p: 3,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
          Teams
        </Typography>
        {canManageTeams && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenCreateModal(true)}
          >
            Create Team
          </Button>
        )}
      </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {teams.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Group sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Teams Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Teams help organize project members and assign roles. Create your first team to get started.
            </Typography>
            {canManageTeams && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenCreateModal(true)}
              >
                Create Team
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {teams.map((team) => (
              <Grid item xs={12} md={6} lg={4} key={team.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {team.name}
                      </Typography>
                      {canManageTeams && (
                        <IconButton size="small">
                          <MoreVert />
                        </IconButton>
                      )}
                    </Box>
                    
                    {team.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {team.description}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Business sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {team.project?.name || 'Unknown Project'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Group sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {team.members?.length || 0} members
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {team.members?.slice(0, 3).map((member) => (
                        <Tooltip key={member.id} title={member.user?.firstName + ' ' + member.user?.lastName}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                            {member.user?.firstName?.[0]}{member.user?.lastName?.[0]}
                          </Avatar>
                        </Tooltip>
                      ))}
                      {team.members && team.members.length > 3 && (
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                          +{team.members.length - 3}
                        </Avatar>
                      )}
                    </Box>
                    
                    <Chip
                      label={team.isActive ? 'Active' : 'Inactive'}
                      color={team.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </CardContent>
                  
                  {canManageTeams && (
                    <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => openEditModalHandler(team)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => openEditModalHandler(team)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteTeam(team.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Create Team Modal */}
        <Dialog open={openCreateModal} onClose={() => setOpenCreateModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create New Team</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Team Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
              <FormControl fullWidth required>
                <InputLabel>Project</InputLabel>
                <Select
                  value={formData.projectId}
                  onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Team Members</InputLabel>
                <Select
                  multiple
                  value={formData.members}
                  onChange={(e) => setFormData({ ...formData, members: e.target.value as string[] })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((userId) => {
                        const user = users.find(u => u.id === userId);
                        return (
                          <Chip
                            key={userId}
                            label={user ? `${user.firstName} ${user.lastName}` : userId}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} variant="contained">Create Team</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Team Modal */}
        <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Team Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
              <FormControl fullWidth>
                <InputLabel>Team Members</InputLabel>
                <Select
                  multiple
                  value={formData.members}
                  onChange={(e) => setFormData({ ...formData, members: e.target.value as string[] })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((userId) => {
                        const user = users.find(u => u.id === userId);
                        return (
                          <Chip
                            key={userId}
                            label={user ? `${user.firstName} ${user.lastName}` : userId}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditTeam} variant="contained">Update Team</Button>
          </DialogActions>
        </Dialog>
    </Layout>
  );
};

export default Teams;